import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import useAxios from "../utils/useAxios";
import {toast} from "react-toastify";

import TournamentExportModal from "../pages/tournaments/export/TournamentExportModal";
import TournamentShareModal from "../pages/tournaments/export/TournamentShareModal";
import { copyTournamentFull } from "../utils/copyTournament";
import {CustomModal} from "./CustomModel/CustomModal";

// copyTournamentFull's onProgress step keys, in the order they actually
// fire - shared with TournamentMenu.js's own copy flow so both compute the
// same real (not animated/fake) progress-bar fraction from the same steps.
export const copyProgressSteps = ["info", "groups", "draws", "rating", "schedule"];

// Step key -> translated toast text.
export const copyProgressLabels = {
    info: "Copying tournament info...",
    groups: "Copying group stages...",
    draws: "Copying brackets...",
    rating: "Copying rating settings...",
    schedule: "Copying schedule...",
};

const TournamentMenuList = ({ tournamentId, onDeleted, horizontal=false, asModal=false, title }) => {
    const [openTournamentMenu, setOpenTournamentMenu] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [menuStyle, setMenuStyle] = useState({}); // ДОБАВЛЕНО
    const { t } = useTranslation();
    const menuRef = useRef();
    const api = useAxios();

    useEffect(() => {
        // in modal mode CustomModal already closes on overlay click/Escape,
        // and there's no floating panel to reposition on scroll
        if (asModal) return;

        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenTournamentMenu(false);
            }
        };

        const handleScroll = (e) => {
            // capture:true below also catches scroll events bubbling up from
            // any scrollable descendant (e.g. the tournament tab strip's own
            // horizontal auto-scroll/swipe, or a horizontally-scrolling
            // table), not just the page itself - closing this floating menu
            // for those was never the intent and just made it flicker/jump
            // while swiping tabs. Only the page's own scroll should close it.
            if (e.target !== document && e.target !== window) return;
            if (openTournamentMenu) setOpenTournamentMenu(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [openTournamentMenu, asModal]);

    const deleteTournament = async () => {
        if (!window.confirm(t("Delete tournament?"))) return;

        try {
            await api.delete(`/api/v1.0/tournaments/${tournamentId}/`);
            toast.success(t("Tournament deleted"));

            if (onDeleted) {
                onDeleted(tournamentId);
            }

            setOpenTournamentMenu(false);
        } catch (error) {
            console.error(error);

            if (error.response?.status === 403) {
                toast.error(t("You do not have permission to delete this tournament"));
            }
        }
    };

    const copyTournament = async () => {
        if (isCopying) return;
        setOpenTournamentMenu(false);
        setIsCopying(true);
        // Live progress in a single updating toast (the site's own green
        // .Toastify__toast--success look, not react-toastify's default
        // blue "info") instead of a blank full-page spinner for the whole
        // several-request copy sequence - each phase updates the same
        // toast rather than stacking a new one. `progress` is a real
        // fraction of copyProgressSteps done, not a fake/animated bar.
        const toastId = toast.success(t(copyProgressLabels.info), { autoClose: false, progress: 0 });
        try {
            const newId = await copyTournamentFull(api, tournamentId, (step) => {
                const stepIndex = copyProgressSteps.indexOf(step);
                toast.update(toastId, {
                    render: t(copyProgressLabels[step]),
                    progress: (stepIndex + 1) / copyProgressSteps.length,
                });
            });
            // No `progress` here - releases controlled mode so autoClose's
            // own countdown bar animates normally instead of sitting stuck
            // at the last controlled value.
            toast.update(toastId, { render: t("Tournament copied"), type: toast.TYPE.SUCCESS, autoClose: 2000 });
            window.location.href = `/tournaments/${newId}/?admin=1`;
        } catch (error) {
            console.error("Copy error 400 details:", error.response?.data || error);
            toast.update(toastId, { render: t("Failed to copy tournament"), type: toast.TYPE.ERROR, autoClose: 4000 });
            setIsCopying(false);
        }
    };

    const actions = [
        { icon: "share-2", label: t("Share"), onClick: () => { setShareOpen(true); setOpenTournamentMenu(false); } },
        { icon: "download", label: t("Export"), onClick: () => { setExportOpen(true); setOpenTournamentMenu(false); } },
        { icon: "copy", label: t("Copy"), onClick: copyTournament },
        { icon: "trash-2", label: t("Delete"), onClick: deleteTournament, danger: true }
    ];

    if (asModal) {
        return (
            <div ref={menuRef} style={{ position: "relative" }}>
                <div className="options dots_menu" onClick={() => setOpenTournamentMenu(true)}>
                    { horizontal ? <FeatherIcon icon="more-horizontal" /> : <FeatherIcon icon="more-vertical" />}
                </div>
                <CustomModal isOpen={openTournamentMenu} onClose={() => setOpenTournamentMenu(false)} compact>
                    <form className="modal-form">
                        {title && (
                            <div className="modal-sticky-header">
                                <h2><FeatherIcon icon="calendar" /> {title}</h2>
                            </div>
                        )}
                        <ul className="user-search-results">
                            {actions.map((a, i) => (
                                <li key={i} onClick={() => { setOpenTournamentMenu(false); a.onClick(); }} style={{ cursor: "pointer" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", color: a.danger ? "var(--bright-alarm)" : undefined }}>
                                        <FeatherIcon icon={a.icon} size={16} /> {a.label}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </form>
                </CustomModal>

                <TournamentExportModal
                    isOpen={exportOpen}
                    onClose={() => setExportOpen(false)}
                    tournamentId={tournamentId}
                />
                <TournamentShareModal
                    isOpen={shareOpen}
                    onClose={() => setShareOpen(false)}
                    tournamentId={tournamentId}
                    tournament={{ name: title }}
                />
            </div>
        );
    }

    return (
        <div ref={menuRef} style={{ position: "relative" }}>
            <div
                className={`options dots_menu ${openTournamentMenu ? 'active' : ''}`}
                onClick={(e) => {
                    if (!openTournamentMenu) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const isDropup = window.innerHeight - rect.bottom < 150;

                        setMenuStyle({
                            position: 'fixed',
                            top: isDropup ? 'auto' : `${rect.bottom + 5}px`,
                            bottom: isDropup ? `${window.innerHeight - rect.top + 5}px` : 'auto',
                            right: `${document.documentElement.clientWidth - rect.right}px`,
                            zIndex: 9999,
                            width: "max-content"
                        });
                    }
                    setOpenTournamentMenu(!openTournamentMenu);
                }}
            >
                { horizontal ? <FeatherIcon icon="more-horizontal" /> : <FeatherIcon icon="more-vertical" />}
            </div>
            <div className="dots_menu_container">
                <div className="relative input">
                    <div
                        className={`options-menu ${openTournamentMenu ? 'block_active' : 'block_inactive'}`}
                        style={{
                            ...(menuStyle.position ? menuStyle : {}),
                            transitionProperty: "opacity, transform, visibility"
                        }}
                    >
                        <div className="options-content-block">
                            {actions.map((a, i) => (
                                <div key={i} className="option" onClick={a.onClick}>
                                    <FeatherIcon icon={a.icon} /> {a.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <TournamentExportModal
                isOpen={exportOpen}
                onClose={() => setExportOpen(false)}
                tournamentId={tournamentId}
            />
            <TournamentShareModal
                isOpen={shareOpen}
                onClose={() => setShareOpen(false)}
                tournamentId={tournamentId}
                tournament={{ name: title }}
            />
        </div>
    );
};

export default TournamentMenuList;
